import { type FC } from "react";
import {
  Button,
  ListItem,
  ListItemText,
  Stack,
  TextField,
} from "@mui/material";
import { tokens } from "src/locales/tokens";
import { useTranslation } from "react-i18next";
import { ContentGoalType } from "src/types/content-goal-type";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "src/store";
import { thunks } from "src/thunks/topic";
import LoadingButton from "@mui/lab/LoadingButton";
import { RouterLink } from "src/components/core/link";
import { Topic } from "src/types/topic";

interface CreateStorylineCategoryTopicItemProps {
  storylineId: string;
  storyLineCategoryId: string;
  showDivider: boolean;
  contentGoalType: ContentGoalType;
  onCreate?: (topic: Topic) => void;
  onCancel?: () => void;
}

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export const CreateStorylineCategoryTopicItem: FC<
  CreateStorylineCategoryTopicItemProps
> = (props) => {
  const {
    showDivider,
    storylineId,
    storyLineCategoryId,
    contentGoalType,
    onCreate: onSave,
    onCancel,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    try {
      const topic = await dispatch(
        thunks.createTopic(strategyId!, {
          name: data.name,
          description: "",
          content: "",
          storyLineId: storylineId,
          storyLineCategoryId: storyLineCategoryId,
          contentGoalType: contentGoalType,
        })
      );
      onSave && onSave(topic);
      toast.success(
        t(tokens.storylinesCategories.details.form.topicUpdatedMessage)
      );
    } catch (error) {
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Submission failed" });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ListItem
        divider={showDivider}
        alignItems="flex-start"
        sx={{ p: 2 }}
      >
        <ListItemText
          disableTypography
          primary={
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.name}
                  fullWidth
                  helperText={errors.name?.message}
                />
              )}
            />
          }
        />
        <Stack
          direction="row"
          alignItems={"start"}
          justifyContent={"flex-start"}
          spacing={1}
        >
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.update)}
          </LoadingButton>
          <Button
            onClick={() => {
              onCancel && onCancel();
            }}
            color="inherit"
            component={RouterLink}
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
        </Stack>
      </ListItem>
    </form>
  );
};
